// Catalog.ID Client — PDF-upload rules for sheet scanning
// ============================================================================
// The decisions the PDF scan path makes BEFORE (and after) any rendering
// happens: is this file a PDF at all, how many of its pages do we look at, at
// what scale do we rasterise them, and what do we tell the person when it does
// not work out.
//
// Kept apart from `pdf-qr.ts` on purpose: that module statically imports pdf.js
// (~1 MB, browser-only, wants a worker), while everything here is pure and runs
// under vitest in plain Node. `card-scan.ts` imports THIS at module load and
// `pdf-qr.ts` only when a PDF is actually picked.

/**
 * Rasterisation target. QR decoding is a pixels-per-module game: the sheet QRs
 * are ~50 mm wide and up to ~101 modules across, so ~200 dpi leaves jsQR just
 * over 4 px per module — comfortably above its floor, without the cost of a
 * 300 dpi render on every page of a kit.
 */
export const PDF_TARGET_DPI = 200;

/** Hard ceiling on the long side of a rendered page, in pixels. A canvas is
 *  4 bytes per pixel and a page render is not cancellable once started, so an
 *  unusual page size must not be allowed to allocate unbounded memory. */
export const PDF_MAX_CANVAS_PX = 4000;

/** Pages the verification page (§6.4) looks at: a full Membership Kit is well
 *  under this, a random document dropped on the page is not worth more. */
export const PDF_MAX_PAGES_DEFAULT = 20;

/** Is this file a PDF rather than a photo or scan? Type first — it is what the
 *  browser actually knows — with the final extension as the fallback for the
 *  platforms that report nothing. */
export function isPdfFile(file: { name?: string; type?: string }): boolean {
    const type = (file.type ?? '').toLowerCase();
    if (type === 'application/pdf' || type === 'application/x-pdf') return true;
    if (type.startsWith('image/')) return false;
    return /\.pdf$/i.test(file.name ?? '');
}

/** How many pages to actually render, given the document and the caller's cap.
 *  Always at least the first page of a non-empty document — a cap of zero is a
 *  programming slip, not an instruction to look at nothing. */
export function pdfPagesToScan(numPages: number, maxPages: number): number {
    if (!(numPages > 0)) return 0;
    const cap = Math.max(1, Math.floor(maxPages));
    return Math.min(numPages, cap);
}

/** Scale to hand `page.getViewport()`: the target dpi, unless that would blow
 *  past the canvas cap. PDF user space is 1/72 inch, so dpi/72 IS the scale. */
export function pdfRenderScale(page: { width: number; height: number }): number {
    const longSidePt = Math.max(page.width, page.height);
    if (!(longSidePt > 0)) return 1;
    return Math.min(PDF_TARGET_DPI / 72, PDF_MAX_CANVAS_PX / longSidePt);
}

export interface NoQrContext {
    fileName: string;
    /** Pages actually rendered and searched. */
    pagesScanned: number;
    /** Pages the document has. */
    totalPages: number;
    /** What the flow was looking for, when it expects one specific sheet —
     *  "Account Unlock Sheet". Omitted where any sheet will do. */
    sheetLabel?: string;
}

/** What to say when the pages we looked at held no QR. Names the part of the
 *  document that was searched, so nobody re-uploads the same file expecting a
 *  different answer. */
export function pdfNoQrMessage(ctx: NoQrContext): string {
    if (ctx.totalPages <= 0 || ctx.pagesScanned <= 0) {
        return `${ctx.fileName} has no pages to read`;
    }
    // Either we stopped short of the end, or the caller is after one specific
    // sheet it expects on page one — both deserve to say where we looked.
    if (ctx.pagesScanned < ctx.totalPages || (ctx.sheetLabel && ctx.pagesScanned === 1)) {
        const what = ctx.sheetLabel ? `${ctx.sheetLabel} QR` : 'QR code';
        const where = ctx.pagesScanned === 1 ? 'the first page' : `the first ${ctx.pagesScanned} pages`;
        return `No ${what} found on ${where} of this PDF`;
    }
    return 'No QR code found in this file';
}

/** Map a pdf.js failure onto one plain line. pdf.js exceptions are ordinary
 *  Errors carrying their class name in `.name`, which is the only stable thing
 *  about them — the messages are developer-facing and sometimes English-only. */
export function pdfErrorMessage(err: unknown, fileName: string): string {
    const name = err instanceof Error ? err.name : '';
    switch (name) {
        case 'PasswordException':
            return `${fileName} is password-protected — save an unprotected copy, `
                + 'or upload a photo of the printed sheet instead';
        case 'InvalidPDFException':
            return `${fileName} is not a readable PDF — it may be damaged or incomplete`;
        case 'ResponseException':
        case 'MissingPDFException':
            return `${fileName} could not be read — the file did not load completely`;
        default:
            return `${fileName} could not be read as a PDF`;
    }
}
